import { useQuery, useMutation } from "@apollo/client";
import { useState } from "react";
import { GET_EVENTS } from "../../server/graphql/queries";
import { DELETE_EVENT } from "../../server/graphql/mutations/events";
import PageContainer from "../../components/dashboard/PageContainer";
import { Link } from "react-router-dom";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { fixUrl } from "../../helpers";
import Loading from "../../components/Loading";
import { format } from "date-fns";

const EventsTable = ({ events, onDelete }) => {
  return (
    <>
      <div className="bg-white">
        <table className="table-auto w-full">
          <thead className="text-left bg-gray-200">
            <tr>
              <th className="pl-2 py-4">Image</th>
              <th>Name</th>
              <th>Views</th>
              <th>DPs</th>
              <th>Event Date</th>
              <th>Date Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.uuid}>
                <td>
                  <div className="flex h-10 w-10 my-4 items-center justify-center rounded-md bg-gray-400 text-white">
                    <img className="h-full w-full rounded-md" src={fixUrl(event.imageUrl)} alt="img" />
                  </div>
                </td>
                <td>{event.title}</td>
                <td>{event.numberOfViews}</td>
                <td>{event.numberOfDps}</td>
                <td>{format(new Date(event.eventDate), "yyyy-MM-dd")}</td>
                <td>{format(new Date(event.createdAt), "yyyy-MM-dd")}</td>
                <td>
                  <button
                    onClick={() => onDelete(event)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const DeleteConfirmModal = ({ event, onConfirm, onCancel, loading }) => {
  if (!event) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <h3 className="text-lg font-medium text-gray-900">Delete Event</h3>
        <p className="mt-2 text-sm text-gray-500">
          Are you sure you want to delete <strong>{event.title}</strong>? This will permanently remove the event, all associated images, and user-generated DPs.
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Events = () => {
  const { loading, error, data } = useQuery(GET_EVENTS);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleteEvent, { loading: deleting }] = useMutation(DELETE_EVENT, {
    refetchQueries: [{ query: GET_EVENTS }],
    onCompleted: () => setEventToDelete(null),
  });

  const events = data?.events || [];
  const suffix = loading ? "" : `(${events.length})`;

  const handleDelete = () => {
    deleteEvent({ variables: { uuid: eventToDelete.uuid } });
  };

  return (
    <>
      <PageContainer
        title={`Events ${suffix}`}
        cta={
          <Link
            to="/dashboard/events/new"
            className="flex items-center justify-center px-5 py-2.5 text-base text-artfulRed whitespace-nowrap hover:underline"
          >
            <PlusIcon className="w-4 h-4" />
            <span className="ml-2">New</span>
          </Link>
        }>
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <EventsTable events={events} onDelete={setEventToDelete} />
        )}
      </PageContainer>
      <DeleteConfirmModal
        event={eventToDelete}
        onConfirm={handleDelete}
        onCancel={() => setEventToDelete(null)}
        loading={deleting}
      />
    </>
  );
};

export default Events;
